/**
 * Business Detail Page with Claim Flow
 * Shows business details and allows claiming unclaimed businesses
 * Route: /business/:id
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MapPin,
  Phone,
  Globe,
  Mail,
  CheckCircle2,
  AlertCircle,
  Shield,
  Navigation,
  ExternalLink,
  Loader2,
  MessageSquare,
  FileText,
  ArrowLeft,
  Map,
  Share2,
  Copy,
  Check
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import GlobalNav from '@/components/GlobalNav';
import Footer from '@/components/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// i18n labels
const LABELS = {
  et: {
    loading: 'Laadimine...',
    notFound: 'Ettevõtet ei leitud',
    backToCatalog: 'Tagasi kataloogi',
    claimed: 'Kinnitatud',
    unclaimed: 'Kinnitamata ettevõte',
    verified: 'Verifitseeritud',
    claimTitle: 'Kinnita see ettevõte',
    claimSubtitle: 'Tõesta omanikuks olemist ja halda oma ettevõtte profiili',
    claimMethodSms: 'SMS kinnitus',
    claimMethodSmsDesc: 'Saadame kinnituskoodi ettevõtte telefonile',
    claimMethodEmail: 'E-maili kinnitus',
    claimMethodEmailDesc: 'Saadame kinnituskoodi ettevõtte domeeni e-mailile',
    claimMethodManual: 'Manuaalne kinnitus',
    claimMethodManualDesc: 'Meie meeskond vaatab üle 24-48h jooksul',
    phone: 'Telefoninumber',
    email: 'E-mail',
    proofDescription: 'Kirjelda kuidas saad tõestada omanikuks olemist',
    contactEmail: 'Kontakt e-mail',
    sendCode: 'Saada kood',
    submitRequest: 'Esita taotlus',
    verifyCode: 'Kinnita kood',
    enterCode: 'Sisesta saadud 6-kohaline kood',
    verify: 'Kinnita',
    codeExpires: 'Kood aegub 10 minuti pärast',
    claimSuccess: 'Ettevõte edukalt kinnitatud!',
    requestSubmitted: 'Taotlus esitatud! Vaatame üle 24-48h jooksul.',
    codeSent: 'Kinnituskood saadetud!',
    navigate: 'Sõida siia',
    openInMaps: 'Ava kaardil',
    share: 'Jaga',
    copied: 'Kopeeritud!',
    categories: 'Kategooriad',
    location: 'Asukoht',
    contact: 'Kontakt',
    description: 'Kirjeldus',
    noDescription: 'Kirjeldus puudub',
    source: 'Allikas',
    reportIssue: 'Teata probleemist',
    optOut: 'Eemalda kataloogist'
  },
  en: {
    loading: 'Loading...',
    notFound: 'Business not found',
    backToCatalog: 'Back to catalog',
    claimed: 'Claimed',
    unclaimed: 'Unclaimed business',
    verified: 'Verified',
    claimTitle: 'Claim this business',
    claimSubtitle: 'Verify ownership and manage your business profile',
    claimMethodSms: 'SMS Verification',
    claimMethodSmsDesc: 'We\'ll send a code to the business phone',
    claimMethodEmail: 'Email Verification',
    claimMethodEmailDesc: 'We\'ll send a code to your company email',
    claimMethodManual: 'Manual Verification',
    claimMethodManualDesc: 'Our team will review within 24-48 hours',
    phone: 'Phone number',
    email: 'Email',
    proofDescription: 'Describe how you can prove ownership',
    contactEmail: 'Contact email',
    sendCode: 'Send Code',
    submitRequest: 'Submit Request',
    verifyCode: 'Verify Code',
    enterCode: 'Enter the 6-digit code you received',
    verify: 'Verify',
    codeExpires: 'Code expires in 10 minutes',
    claimSuccess: 'Business claimed successfully!',
    requestSubmitted: 'Request submitted! We\'ll review within 24-48 hours.',
    codeSent: 'Verification code sent!',
    navigate: 'Navigate',
    openInMaps: 'Open in Maps',
    share: 'Share',
    copied: 'Copied!',
    categories: 'Categories',
    location: 'Location',
    contact: 'Contact',
    description: 'Description',
    noDescription: 'No description available',
    source: 'Source',
    reportIssue: 'Report an issue',
    optOut: 'Remove from catalog'
  },
  fi: {
    loading: 'Ladataan...',
    notFound: 'Yritystä ei löytynyt',
    backToCatalog: 'Takaisin katalogiin',
    claimed: 'Vahvistettu',
    unclaimed: 'Vahvistamaton yritys',
    verified: 'Verifioitu',
    claimTitle: 'Vahvista tämä yritys',
    claimSubtitle: 'Todista omistajuus ja hallitse yritysprofiiliasi',
    claimMethodSms: 'SMS-vahvistus',
    claimMethodSmsDesc: 'Lähetämme koodin yrityksen puhelimeen',
    claimMethodEmail: 'Sähköpostivahvistus',
    claimMethodEmailDesc: 'Lähetämme koodin yrityksen sähköpostiin',
    claimMethodManual: 'Manuaalinen vahvistus',
    claimMethodManualDesc: 'Tiimimme tarkistaa 24-48 tunnin kuluessa',
    phone: 'Puhelinnumero',
    email: 'Sähköposti',
    proofDescription: 'Kuvaile miten voit todistaa omistajuuden',
    contactEmail: 'Yhteyssähköposti',
    sendCode: 'Lähetä koodi',
    submitRequest: 'Lähetä pyyntö',
    verifyCode: 'Vahvista koodi',
    enterCode: 'Syötä saamasi 6-numeroinen koodi',
    verify: 'Vahvista',
    codeExpires: 'Koodi vanhenee 10 minuutissa',
    claimSuccess: 'Yritys vahvistettu onnistuneesti!',
    requestSubmitted: 'Pyyntö lähetetty! Tarkistamme 24-48 tunnin kuluessa.',
    codeSent: 'Vahvistuskoodi lähetetty!',
    navigate: 'Navigoi',
    openInMaps: 'Avaa kartalla',
    share: 'Jaa',
    copied: 'Kopioitu!',
    categories: 'Kategoriat',
    location: 'Sijainti',
    contact: 'Yhteystiedot',
    description: 'Kuvaus',
    noDescription: 'Kuvausta ei saatavilla',
    source: 'Lähde',
    reportIssue: 'Ilmoita ongelmasta',
    optOut: 'Poista katalogista'
  },
  ru: {
    loading: 'Загрузка...',
    notFound: 'Компания не найдена',
    backToCatalog: 'Назад в каталог',
    claimed: 'Подтверждено',
    unclaimed: 'Неподтверждённая компания',
    verified: 'Верифицировано',
    claimTitle: 'Подтвердить эту компанию',
    claimSubtitle: 'Подтвердите владение и управляйте профилем',
    claimMethodSms: 'SMS подтверждение',
    claimMethodSmsDesc: 'Отправим код на телефон компании',
    claimMethodEmail: 'Email подтверждение',
    claimMethodEmailDesc: 'Отправим код на email компании',
    claimMethodManual: 'Ручная проверка',
    claimMethodManualDesc: 'Наша команда проверит за 24-48 часов',
    phone: 'Телефон',
    email: 'Email',
    proofDescription: 'Опишите как вы можете подтвердить владение',
    contactEmail: 'Контактный email',
    sendCode: 'Отправить код',
    submitRequest: 'Отправить заявку',
    verifyCode: 'Подтвердить код',
    enterCode: 'Введите полученный 6-значный код',
    verify: 'Подтвердить',
    codeExpires: 'Код истекает через 10 минут',
    claimSuccess: 'Компания успешно подтверждена!',
    requestSubmitted: 'Заявка отправлена! Проверим за 24-48 часов.',
    codeSent: 'Код подтверждения отправлен!',
    navigate: 'Навигация',
    openInMaps: 'Открыть на карте',
    share: 'Поделиться',
    copied: 'Скопировано!',
    categories: 'Категории',
    location: 'Адрес',
    contact: 'Контакты',
    description: 'Описание',
    noDescription: 'Описание отсутствует',
    source: 'Источник',
    reportIssue: 'Сообщить о проблеме',
    optOut: 'Удалить из каталога'
  },
  nb: {
    loading: 'Laster…',
    notFound: 'Bedriften ble ikke funnet',
    backToCatalog: 'Tilbake til katalogen',
    claimed: 'Bekreftet',
    unclaimed: 'Ikke bekreftet bedrift',
    verified: 'Verifisert',
    claimTitle: 'Krev denne bedriften',
    claimSubtitle: 'Bekreft eierskap og administrer bedriftens profil',
    claimMethodSms: 'SMS-bekreftelse',
    claimMethodSmsDesc: 'Vi sender en kode til bedriftens telefonnummer',
    claimMethodEmail: 'E-postbekreftelse',
    claimMethodEmailDesc: 'Vi sender en kode til bedriftens domenepostadresse',
    claimMethodManual: 'Manuell bekreftelse',
    claimMethodManualDesc: 'Teamet vårt gjennomgår det innen 24–48 timer',
    phone: 'Telefonnummer',
    email: 'E-post',
    proofDescription: 'Beskriv hvordan du kan bevise eierskap',
    contactEmail: 'Kontakt-e-post',
    sendCode: 'Send kode',
    submitRequest: 'Send forespørsel',
    verifyCode: 'Bekreft kode',
    enterCode: 'Oppgi den 6-sifrede koden',
    verify: 'Bekreft',
    codeExpires: 'Koden utløper om 10 minutter',
    claimSuccess: 'Bedriften er bekreftet!',
    codeSent: 'Bekreftelseskode sendt!',
    navigate: 'Naviger',
    openInMaps: 'Åpne i kart',
    share: 'Del',
    copied: 'Kopiert!',
    categories: 'Kategorier',
    location: 'Adresse',
    contact: 'Kontakt',
    description: 'Beskrivelse',
    noDescription: 'Ingen beskrivelse',
    source: 'Kilde',
    reportIssue: 'Rapporter problem',
    optOut: 'Fjern fra katalog'
  }
};

const CATEGORY_LABELS = {
  services: { et: 'Teenused', en: 'Services', fi: 'Palvelut', ru: 'Услуги', nb: 'Tjenester' },
  auto_repair: { et: 'Autoremont', en: 'Auto Repair', fi: 'Autokorjaamo', ru: 'Автосервис', nb: 'Bilverksted' },
  b2b: { et: 'Ehitus/B2B', en: 'Construction/B2B', fi: 'Rakennus/B2B', ru: 'Строительство/B2B', nb: 'Bygg / B2B' },
  transport: { et: 'Transport', en: 'Transport', fi: 'Kuljetus', ru: 'Транспорт', nb: 'Transport' },
  accommodation: { et: 'Majutus', en: 'Accommodation', fi: 'Majoitus', ru: 'Жильё', nb: 'Overnatting' },
  equipment: { et: 'Seadmed', en: 'Equipment', fi: 'Laitteet', ru: 'Оборудование', nb: 'Utstyr' }
};

// Detect Apple device for Maps fallback
const isAppleDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || '';
  return /iPad|iPhone|iPod|Macintosh/.test(ua) && !window.MSStream;
};

// Claim Dialog Component
function ClaimDialog({ business, isOpen, onClose, labels, language }) {
  const [step, setStep] = useState('method'); // method, verify, success
  const [method, setMethod] = useState('');
  const [phone, setPhone] = useState(business?.phone_public || '');
  const [email, setEmail] = useState('');
  const [proofDescription, setProofDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [code, setCode] = useState('');
  const [claimAttemptId, setClaimAttemptId] = useState('');
  const [loading, setLoading] = useState(false);
  
  const hasPhone = !!business?.phone_public;
  const hasWebsite = !!business?.website;
  
  const handleSendCode = async () => {
    setLoading(true);
    
    try {
      const payload = {
        method: method === 'sms' ? 'SMS' : 'EMAIL_DOMAIN',
        phone: method === 'sms' ? phone : undefined,
        email: method === 'email' ? email : undefined
      };
      
      const response = await fetch(`${API_URL}/api/businesses/${business.id}/claim/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.claim_attempt_id) {
        setClaimAttemptId(data.claim_attempt_id);
        setStep('verify');
        toast.success(labels.codeSent);
      } else {
        toast.error(data.detail || 'Failed to send code');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyCode = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/businesses/${business.id}/claim/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_attempt_id: claimAttemptId,
          code: code
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStep('success');
        toast.success(labels.claimSuccess);
      } else {
        toast.error(data.detail || 'Invalid code');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleManualRequest = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/businesses/${business.id}/claim/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_id: business.id,
          proof_description: proofDescription,
          contact_email: contactEmail
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStep('success');
        toast.success(labels.requestSubmitted);
      } else {
        toast.error(data.detail || 'Failed to submit request');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{labels.claimTitle}</DialogTitle>
          <DialogDescription>{labels.claimSubtitle}</DialogDescription>
        </DialogHeader>
        
        {step === 'method' && (
          <div className="space-y-4">
            {/* SMS Option */}
            {hasPhone && (
              <Card 
                className={`cursor-pointer transition-all ${method === 'sms' ? 'ring-2 ring-orange-500' : ''}`}
                onClick={() => setMethod('sms')}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{labels.claimMethodSms}</h4>
                      <p className="text-sm text-muted-foreground">{labels.claimMethodSmsDesc}</p>
                      {method === 'sms' && (
                        <Input
                          placeholder={labels.phone}
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="mt-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Email Option */}
            {hasWebsite && (
              <Card 
                className={`cursor-pointer transition-all ${method === 'email' ? 'ring-2 ring-orange-500' : ''}`}
                onClick={() => setMethod('email')}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="font-semibold">{labels.claimMethodEmail}</h4>
                      <p className="text-sm text-muted-foreground">{labels.claimMethodEmailDesc}</p>
                      {method === 'email' && (
                        <Input
                          placeholder={labels.email}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="mt-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Manual Option */}
            <Card 
              className={`cursor-pointer transition-all ${method === 'manual' ? 'ring-2 ring-orange-500' : ''}`}
              onClick={() => setMethod('manual')}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-purple-500 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold">{labels.claimMethodManual}</h4>
                    <p className="text-sm text-muted-foreground">{labels.claimMethodManualDesc}</p>
                    {method === 'manual' && (
                      <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <Textarea
                          placeholder={labels.proofDescription}
                          value={proofDescription}
                          onChange={(e) => setProofDescription(e.target.value)}
                          rows={3}
                        />
                        <Input
                          placeholder={labels.contactEmail}
                          type="email"
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Submit Button */}
            <Button 
              className="w-full"
              disabled={!method || loading || (method === 'manual' && (!proofDescription || !contactEmail))}
              onClick={method === 'manual' ? handleManualRequest : handleSendCode}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {method === 'manual' ? labels.submitRequest : labels.sendCode}
            </Button>
          </div>
        )}
        
        {step === 'verify' && (
          <div className="space-y-4">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="font-semibold">{labels.verifyCode}</h3>
              <p className="text-sm text-muted-foreground">{labels.enterCode}</p>
            </div>
            
            <Input
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-widest"
              maxLength={6}
            />
            
            <p className="text-xs text-center text-muted-foreground">{labels.codeExpires}</p>
            
            <Button 
              className="w-full"
              disabled={code.length !== 6 || loading}
              onClick={handleVerifyCode}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {labels.verify}
            </Button>
          </div>
        )}
        
        {step === 'success' && (
          <div className="text-center py-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">{labels.claimSuccess}</h3>
            <Button onClick={onClose} className="mt-4">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function BusinessDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const labels = LABELS[language] || LABELS.en;
  
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimDialogOpen, setClaimDialogOpen] = useState(searchParams.get('claim') === 'true');
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await fetch(`${API_URL}/api/businesses/id/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          setBusiness(data);
        } else {
          setBusiness(null);
        }
      } catch (error) {
        console.error('Error fetching business:', error);
        setBusiness(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusiness();
  }, [id]);
  
  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: business?.name,
          text: `${business?.name} - ${business?.city}, ${business?.country_code}`,
          url
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(labels.copied);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }
  
  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <GlobalNav />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{labels.notFound}</h2>
            <Button asChild>
              <Link to="/businesses">{labels.backToCatalog}</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  const isClaimed = business.status === 'CLAIMED';
  const isApple = isAppleDevice();
  const lat = business.location?.lat;
  const lng = business.location?.lng;
  
  const navigateUrl = isApple 
    ? `https://maps.apple.com/?daddr=${lat},${lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    
  const mapsUrl = isApple
    ? `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(business.name)}`
    : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  
  return (
    <>
      <SEOHead 
        title={`${business.name} | KEIKKA`}
        description={`${business.name} - ${business.city}, ${business.country_code}`}
        image="/og-business-preview.png"
      />
      
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <GlobalNav />
        
        <main className="flex-1">
          {/* Header */}
          <div className={`py-8 ${isClaimed ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-gray-500 to-slate-500'} text-white`}>
            <div className="max-w-4xl mx-auto px-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-white/20 mb-4"
                asChild
              >
                <Link to="/businesses">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {labels.backToCatalog}
                </Link>
              </Button>
              
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold">{business.name}</h1>
                    {isClaimed ? (
                      <Badge className="bg-white/20 text-white">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {labels.claimed}
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-500 text-yellow-900">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {labels.unclaimed}
                      </Badge>
                    )}
                    {business.is_verified && (
                      <Badge className="bg-blue-500 text-white">
                        <Shield className="w-3 h-3 mr-1" />
                        {labels.verified}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 text-white/90">
                    <MapPin className="w-4 h-4" />
                    <span>{business.address_text || `${business.city || business.region || 'Unknown'}, ${business.country_code}`}</span>
                  </div>
                </div>
                
                <Button 
                  variant="secondary"
                  size="sm"
                  onClick={handleShare}
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                  {labels.share}
                </Button>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="md:col-span-2 space-y-6">
                {/* Categories */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{labels.categories}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {business.categories?.map((cat, i) => (
                        <Badge key={i} variant="secondary">
                          {CATEGORY_LABELS[cat]?.[language] || cat}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Description */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{labels.description}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {business.description || labels.noDescription}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Map Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{labels.location}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-3">
                      <Button asChild className="flex-1">
                        <a href={navigateUrl} target="_blank" rel="noopener noreferrer">
                          <Navigation className="w-4 h-4 mr-2" />
                          {labels.navigate}
                        </a>
                      </Button>
                      <Button variant="outline" asChild className="flex-1">
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                          <Map className="w-4 h-4 mr-2" />
                          {labels.openInMaps}
                        </a>
                      </Button>
                    </div>
                    
                    {/* Mini map preview */}
                    {lat && lng && (
                      <div className="mt-4 h-48 bg-gray-200 rounded-lg overflow-hidden">
                        <iframe
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          style={{ border: 0 }}
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${lng+0.01},${lat+0.01}&layer=mapnik&marker=${lat},${lng}`}
                          allowFullScreen
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Sidebar */}
              <div className="space-y-6">
                {/* Contact Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{labels.contact}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isClaimed ? (
                      <>
                        {business.phone_public && (
                          <a 
                            href={`tel:${business.phone_public}`}
                            className="flex items-center gap-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition"
                          >
                            <Phone className="w-5 h-5 text-green-600" />
                            <span className="font-medium">{business.phone_public}</span>
                          </a>
                        )}
                        {business.email_public && (
                          <a 
                            href={`mailto:${business.email_public}`}
                            className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
                          >
                            <Mail className="w-5 h-5 text-blue-600" />
                            <span className="font-medium truncate">{business.email_public}</span>
                          </a>
                        )}
                        {business.website && (
                          <a 
                            href={business.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition"
                          >
                            <Globe className="w-5 h-5 text-purple-600" />
                            <span className="font-medium truncate">{business.website.replace(/^https?:\/\//, '')}</span>
                            <ExternalLink className="w-4 h-4 ml-auto" />
                          </a>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-4">
                          {labels.unclaimed}
                        </p>
                        <Button 
                          className="w-full bg-orange-500 hover:bg-orange-600"
                          onClick={() => setClaimDialogOpen(true)}
                        >
                          {labels.claimTitle}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Claim CTA for unclaimed */}
                {!isClaimed && (
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="p-4 text-center">
                      <h3 className="font-semibold mb-2">{labels.claimTitle}</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {labels.claimSubtitle}
                      </p>
                      <Button 
                        className="w-full"
                        onClick={() => setClaimDialogOpen(true)}
                      >
                        {labels.claimTitle}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {/* Source info */}
                <div className="text-xs text-muted-foreground">
                  <p>{labels.source}: {business.source || 'OSM'}</p>
                </div>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
      
      {/* Claim Dialog */}
      <ClaimDialog 
        business={business}
        isOpen={claimDialogOpen}
        onClose={() => setClaimDialogOpen(false)}
        labels={labels}
        language={language}
      />
    </>
  );
}
